import { Form, twMerge } from '@faceup/ui'
import { Button, Input, Typography } from '@faceup/ui-base'
import { isEmail } from '@faceup/utils'
import { type ComponentProps, useState } from 'react'
import { PageTemplateUnlogged } from '../PageTemplateUnlogged'
import { TestAccountsModal } from './TestAccounts'
import { useLogin } from './useLogin'

const { Title: AntTitle } = Typography

export type FormType = 'login' | '2fa'

export const LoginView = () => {
  const [loading, setLoading] = useState(false)

  const [showTestAccounts, setShowTestAccounts] = useState(false)
  const {
    serverLoginFormError,
    login,
    code,
    setCode,
    email,
    setEmail,
    password,
    setPassword,
    formType,
  } = useLogin(setLoading)

  const hasLoginFormValidationFailed = () => {
    let hasFailed = false

    const isValidPassword = Boolean(password.value)
    if (!isValidPassword) {
      setPassword({ ...password, error: true })
      hasFailed = true
    }

    const isValidEmail = isEmail(email.value.trim())
    if (!isValidEmail) {
      setEmail({ ...email, error: true })
      hasFailed = true
    }

    return hasFailed
  }

  const has2FAFormValidationFailed = () => {
    const isCodeValid = code.value.split(' ').join('').length === 6
    if (!isCodeValid) {
      setCode({ ...code, error: true })

      return true
    }

    return false
  }

  const toggleTestAccounts = () => {
    if (import.meta.env.VITE_ENVIRONMENT !== 'production') {
      setShowTestAccounts(!showTestAccounts)
    }
  }

  return (
    <PageTemplateUnlogged
      isFooterShown
      contentUnderCard={
        import.meta.env.VITE_ENVIRONMENT !== 'production' && (
          <TestAccountsModal
            open={showTestAccounts}
            setOpen={setShowTestAccounts}
            setEmail={value => setEmail({ value, error: false })}
            setPassword={value => setPassword({ value, error: false })}
            login={login}
          />
        )
      }
    >
      {formType === 'login' ? (
        <>
          <Title data-test='login-title' level={3}>
            Administration login
          </Title>
          <Form
            onSubmit={() => {
              if (hasLoginFormValidationFailed() || loading) {
                return
              }

              login()
            }}
          >
            <Form.Item
              label='E-mail'
              formItemProps={{
                'data-test': 'email-group',
              }}
              errorMessage={email.error && 'Invalid e-mail'}
            >
              <Input
                size='large'
                type='email'
                autoComplete='email'
                value={email.value}
                onChange={({ target: { value } }) => setEmail({ value, error: false })}
              />
            </Form.Item>

            <Form.Item
              label={
                <div className='flex flex-1 justify-between'>
                  <div onDoubleClick={() => toggleTestAccounts()}>Password</div>
                </div>
              }
              formItemProps={{
                'data-test': 'password-group',
              }}
              errorMessage={
                (password.error || serverLoginFormError) && 'Password or email is incorrect'
              }
            >
              <Input.Password
                value={password.value}
                autoComplete='current-password'
                size='large'
                onChange={({ target: { value } }) => setPassword({ value, error: false })}
              />
            </Form.Item>

            <Button
              type='primary'
              htmlType='submit'
              data-test='submit-button'
              block
              loading={loading}
            >
              Login
            </Button>
          </Form>
        </>
      ) : formType === '2fa' ? (
        <>
          <Title data-test='login-title' level={3}>
            Two factor code
          </Title>
          <Form
            onClick={() => {
              if (hasLoginFormValidationFailed() || has2FAFormValidationFailed() || loading) {
                return
              }

              login()
            }}
          >
            <Form.Item label='2FA code' errorMessage={code.error && 'Invalid 2FA code'}>
              <Input
                size='large'
                type='text'
                inputMode='numeric'
                autoComplete='off'
                value={code.value}
                onChange={({ target: { value } }) => {
                  setCode({ value, error: false })
                }}
              />
            </Form.Item>
            <Button type='primary' htmlType='submit' block>
              Login
            </Button>
          </Form>
        </>
      ) : null}
    </PageTemplateUnlogged>
  )
}

// TODO: styled leftover. Reused element extracted so its shared classes live in one
// place. Ideally done differently, kept like this to minimize breaking changes.
const Title = ({ className, ...props }: ComponentProps<typeof AntTitle>) => (
  <AntTitle className={twMerge('mb-5 text-center', className)} {...props} />
)
